import { createServerFn } from "@tanstack/react-start";
import { render } from "@react-email/render";
import { createHash, randomBytes } from "node:crypto";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import {
  EmailVerificationEmail,
  subject as emailVerificationSubject,
} from "@/lib/email-templates/transactional/email_verification";

const RESEND_ENDPOINT = "https://connector-gateway.lovable.dev/resend/emails";
const FROM_ADDRESS = "CapitalDart <noreply@capitaldart.com>";
const REPLY_TO = "support@capitaldart.com";
const VERIFY_URL_BASE = "https://capitaldart.com/verify-email";
const CODE_TTL_MINUTES = 30;
const RATE_LIMIT_SECONDS = 60;
// Per email address ceiling for the unauthenticated resend form, so it
// cannot be used to mail-bomb somebody who did sign up.
const RESEND_MAX_PER_HOUR = 3;

export const EMAIL_UNVERIFIED_ERROR = "EMAIL_UNVERIFIED";

export function emailUnverifiedError(): Error {
  const err = new Error(
    "Confirm your email first. Check the banner at the top of the page.",
  ) as Error & { code?: string };
  err.code = EMAIL_UNVERIFIED_ERROR;
  return err;
}

export function isEmailUnverifiedError(err: unknown): boolean {
  if (!err) return false;
  const msg =
    err instanceof Error
      ? err.message
      : typeof err === "string"
        ? err
        : "";
  return (
    msg.includes("Confirm your email first") ||
    msg.includes(EMAIL_UNVERIFIED_ERROR)
  );
}

function generateToken(): string {
  return randomBytes(32).toString("hex");
}

function hashToken(token: string): string {
  return createHash("sha256").update(token, "utf8").digest("hex");
}

function buildVerifyUrl(token: string): string {
  // Use a path segment as the primary form. Query strings are more likely to
  // be stripped or mangled by corporate email scanners (Outlook SafeLinks,
  // Mimecast, Proofpoint) than path segments. The query-param form
  // (/verify-email?token=...) still works for older emails already sent.
  return `${VERIFY_URL_BASE}/${encodeURIComponent(token)}`;
}

const CONSENT_PATH = "/.lovable/oauth/consent";
const SITE_ORIGIN = "https://capitaldart.com";

/**
 * Only a same-origin relative path pointing at the OAuth consent screen is
 * ever honoured as a post-confirmation destination. Anything else (absolute
 * URLs, protocol-relative //host, other app paths) is rejected.
 */
export function sanitizeConsentRedirect(
  value: unknown,
): string | null {
  if (typeof value !== "string") return null;
  const v = value.trim();
  if (!v.startsWith(CONSENT_PATH)) return null;
  if (v.startsWith("//") || v.includes("://") || /[\r\n]/.test(v)) return null;
  return v.slice(0, 500);
}


export type VerificationStatus = {
  verified: boolean;
  verifiedAt: string | null;
  email: string | null;
  lastCodeSentAt: string | null;
};

export const getVerificationStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<VerificationStatus> => {
    const { userId, claims } = context;
    const { data: prof } = await supabaseAdmin
      .from("profiles")
      .select("email_verified, email_verified_at")
      .eq("user_id", userId)
      .maybeSingle();
    const { data: lastCode } = await supabaseAdmin
      .from("email_verification_codes")
      .select("created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    const email =
      typeof claims?.email === "string" ? (claims.email as string) : null;
    return {
      verified: (prof?.email_verified as boolean | undefined) ?? false,
      verifiedAt: (prof?.email_verified_at as string | null | undefined) ?? null,
      email,
      lastCodeSentAt: (lastCode?.created_at as string | null | undefined) ?? null,
    };
  });

export type RequestVerificationResult =
  | { ok: true; sent: true }
  | { ok: false; reason: "already_verified" | "rate_limited" | "no_email" | "send_failed"; message: string };

export const requestEmailVerification = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<RequestVerificationResult> => {
    const { userId, claims } = context;
    const email =
      typeof claims?.email === "string" ? (claims.email as string).trim().toLowerCase() : "";
    if (!email) {
      return { ok: false, reason: "no_email", message: "No email address on file." };
    }

    const { data: prof } = await supabaseAdmin
      .from("profiles")
      .select("email_verified")
      .eq("user_id", userId)
      .maybeSingle();
    if ((prof?.email_verified as boolean | undefined) === true) {
      return { ok: false, reason: "already_verified", message: "Email already verified." };
    }

    const { data: recent } = await supabaseAdmin
      .from("email_verification_codes")
      .select("created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (recent?.created_at) {
      const ageMs = Date.now() - new Date(recent.created_at as string).getTime();
      if (ageMs < RATE_LIMIT_SECONDS * 1000) {
        const wait = Math.ceil((RATE_LIMIT_SECONDS * 1000 - ageMs) / 1000);
        return {
          ok: false,
          reason: "rate_limited",
          message: `Please wait ${wait} seconds before requesting another link.`,
        };
      }
    }

    await supabaseAdmin
      .from("email_verification_codes")
      .update({ consumed_at: new Date().toISOString() })
      .eq("user_id", userId)
      .is("consumed_at", null);

    const token = generateToken();
    const tokenHash = hashToken(token);
    const expiresAt = new Date(Date.now() + CODE_TTL_MINUTES * 60_000).toISOString();
    const { error: insertErr } = await supabaseAdmin
      .from("email_verification_codes")
      .insert({
        user_id: userId,
        code: null as unknown as string,
        token_hash: tokenHash,
        expires_at: expiresAt,
      });
    if (insertErr) {
      return { ok: false, reason: "send_failed", message: insertErr.message };
    }

    const verifyUrl = buildVerifyUrl(token);
    const html = await render(
      EmailVerificationEmail({ verifyUrl, recipient: email }),
    );

    const resendKey = process.env.RESEND_API_KEY_1 ?? process.env.RESEND_API_KEY;
    const lovableKey = process.env.LOVABLE_API_KEY;
    if (!resendKey || !lovableKey) {
      await supabaseAdmin.from("email_send_log").insert({
        template_name: "email_verification",
        recipient_email: email,
        status: "failed",
        error_message: "missing_email_config",
      });
      return {
        ok: false,
        reason: "send_failed",
        message: "Email service is not configured. Please contact support.",
      };
    }

    try {
      const res = await fetch(RESEND_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${lovableKey}`,
          "X-Connection-Api-Key": resendKey,
        },
        body: JSON.stringify({
          from: FROM_ADDRESS,
          to: [email],
          subject: emailVerificationSubject,
          html,
          reply_to: REPLY_TO,
        }),
      });
      const body = await res.text();
      if (!res.ok) {
        await supabaseAdmin.from("email_send_log").insert({
          template_name: "email_verification",
          recipient_email: email,
          status: "failed",
          error_message: body.slice(0, 500),
          metadata: { http_status: res.status, provider: "resend" } as never,
        });
        return {
          ok: false,
          reason: "send_failed",
          message: "We could not send the email right now. Try again in a moment.",
        };
      }
      let providerId: string | null = null;
      try {
        providerId = (JSON.parse(body) as { id?: string }).id ?? null;
      } catch {
        providerId = null;
      }
      await supabaseAdmin.from("email_send_log").insert({
        template_name: "email_verification",
        recipient_email: email,
        status: "sent",
        message_id: providerId,
        metadata: { provider: "resend" } as never,
      });
      return { ok: true, sent: true };
    } catch (err) {
      const msg = err instanceof Error ? err.message : "send_failed";
      await supabaseAdmin.from("email_send_log").insert({
        template_name: "email_verification",
        recipient_email: email,
        status: "failed",
        error_message: msg.slice(0, 500),
      });
      return {
        ok: false,
        reason: "send_failed",
        message: "We could not send the email right now. Try again in a moment.",
      };
    }
  });

export type ConsumeVerificationResult =
  | {
      ok: true;
      userId: string;
      actionLink: string | null;
      magiclinkFailed?: boolean;
      redirectTo?: string | null;
    }

  | {
      ok: false;
      reason: "invalid" | "expired" | "confirm_failed";
      message: string;
    };

async function logVerificationEvent(
  userId: string | null,
  eventType: string,
  properties: Record<string, unknown>,
): Promise<void> {
  try {
    await supabaseAdmin.from("events").insert({
      ...(userId ? { user_id: userId } : {}),
      event_type: eventType,
      properties: properties as never,
    });
  } catch {
    // never let telemetry break verification
  }
}

export const CONFIRM_FAILED_MESSAGE =
  "Something went wrong confirming your account. We have been notified and will fix it promptly. If you want to reach us directly, email hello@capitaldart.com and we will get you set up.";

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Authoritative confirmation with two retries and brief backoff, so a
 * transient GoTrue hiccup does not strand an account.
 */
async function confirmEmailWithRetry(
  userId: string,
): Promise<
  | { ok: true }
  | { ok: false; message: string; status: number | null; attempts: number }
> {
  const backoffMs = [300, 900];
  let lastMessage = "unknown error";
  let lastStatus: number | null = null;
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      const { error } = await supabaseAdmin.auth.admin.updateUserById(userId, {
        email_confirm: true,
      });
      if (!error) return { ok: true };
      lastMessage = error.message;
      lastStatus = (error as { status?: number }).status ?? null;
    } catch (err) {
      lastMessage = err instanceof Error ? err.message : String(err);
      lastStatus = null;
    }
    const wait = backoffMs[attempt - 1];
    if (wait) await sleep(wait);
  }
  return { ok: false, message: lastMessage, status: lastStatus, attempts: 3 };
}

/** True when Supabase Auth or the profile already treats this account as confirmed. */
async function isAccountConfirmed(userId: string): Promise<boolean> {
  try {
    const { data } = await supabaseAdmin.auth.admin.getUserById(userId);
    if (data?.user?.email_confirmed_at) return true;
  } catch {
    // fall through to the profile check
  }
  const { data: prof } = await supabaseAdmin
    .from("profiles")
    .select("email_verified")
    .eq("user_id", userId)
    .maybeSingle();
  return (prof?.email_verified as boolean | undefined) === true;
}

/**
 * Convenience only: mint a one-time magic link so opening the confirmation
 * link on any browser or device also signs the person in. Never fatal.
 */
async function mintSignInLink(
  userId: string,
  redirectPath?: string | null,
): Promise<{ actionLink: string | null; magiclinkFailed: boolean }> {
  // Only the consent screen may override the default landing page.
  const safePath = sanitizeConsentRedirect(redirectPath) ?? "/dashboard";
  try {
    const { data: userRes, error: userError } =
      await supabaseAdmin.auth.admin.getUserById(userId);
    const email = userRes?.user?.email ?? null;
    if (userError || !email) {
      await logVerificationEvent(userId, "verification_magiclink_failed", {
        message: userError?.message ?? "no email on user record",
        status: (userError as { status?: number } | null)?.status ?? null,
      });
      return { actionLink: null, magiclinkFailed: true };
    }
    const { data: linkRes, error: linkError } =
      await supabaseAdmin.auth.admin.generateLink({
        type: "magiclink",
        email,
        options: { redirectTo: `${SITE_ORIGIN}${safePath}` },
      });

    const actionLink = linkRes?.properties?.action_link ?? null;
    if (linkError || !actionLink) {
      await logVerificationEvent(userId, "verification_magiclink_failed", {
        message: linkError?.message ?? "action_link was null",
        status: (linkError as { status?: number } | null)?.status ?? null,
      });
      return { actionLink: null, magiclinkFailed: true };
    }
    return { actionLink, magiclinkFailed: false };
  } catch (err) {
    await logVerificationEvent(userId, "verification_magiclink_failed", {
      message: err instanceof Error ? err.message : String(err),
      status: null,
    });
    return { actionLink: null, magiclinkFailed: true };
  }
}

export const recordVerificationEvent = createServerFn({ method: "POST" })
  .inputValidator((input: { userId: string; eventType: string }) => ({
    userId: typeof input?.userId === "string" ? input.userId : "",
    eventType:
      input?.eventType === "verification_magiclink_followed"
        ? "verification_magiclink_followed"
        : "",
  }))
  .handler(async ({ data }) => {
    if (!data.eventType) return { ok: false as const };
    await logVerificationEvent(data.userId || null, data.eventType, {
      source: "verify_email_view",
    });
    return { ok: true as const };
  });

export const consumeVerificationToken = createServerFn({ method: "POST" })
  .inputValidator((input: { token: string }) => {
    const raw = typeof input?.token === "string" ? input.token.trim() : "";
    if (!/^[a-f0-9]{64}$/.test(raw)) {
      const err = new Error("This confirmation link is not valid.") as Error & { code?: string };
      err.code = "invalid_token";
      throw err;
    }
    return { token: raw };
  })
  .handler(async ({ data }): Promise<ConsumeVerificationResult> => {
    const hash = hashToken(data.token);
    const { data: row } = await supabaseAdmin
      .from("email_verification_codes")
      .select("id, user_id, expires_at, consumed_at, redirect_to")

      .eq("token_hash", hash)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!row) {
      return {
        ok: false,
        reason: "invalid",
        message:
          "This confirmation link is no longer valid. It may have already been used or been replaced by a newer one.",
      };
    }
    if (new Date(row.expires_at as string).getTime() < Date.now()) {
      return {
        ok: false,
        reason: "expired",
        message: "This confirmation link has expired. Request a new one and try again.",
      };
    }

    const now = new Date().toISOString();
    const userId = row.user_id as string;
    // Stored at signup: the OAuth consent screen the person came from.
    const storedRedirect = sanitizeConsentRedirect(
      (row as { redirect_to?: string | null }).redirect_to,
    );

    // Idempotent replay: the same link clicked twice, or prefetched by a
    // corporate mail scanner and then clicked by the person, must look
    // identical to the first click. Only when the token was consumed
    // inside its own TTL window and the account really is confirmed.
    if (row.consumed_at) {
      const consumedInWindow =
        new Date(row.consumed_at as string).getTime() <=
        new Date(row.expires_at as string).getTime();
      if (consumedInWindow && (await isAccountConfirmed(userId))) {
        const { actionLink, magiclinkFailed } = await mintSignInLink(
          userId,
          storedRedirect,
        );
        return {
          ok: true,
          userId,
          actionLink,
          magiclinkFailed,
          redirectTo: storedRedirect,
        };

      }
      return {
        ok: false,
        reason: "invalid",
        message:
          "This confirmation link is no longer valid. It may have already been used or been replaced by a newer one.",
      };
    }

    await supabaseAdmin
      .from("email_verification_codes")
      .update({ consumed_at: now })
      .eq("id", row.id as string);
    await supabaseAdmin
      .from("profiles")
      .update({ email_verified: true, email_verified_at: now })
      .eq("user_id", userId);

    // Authoritative confirmation: mark the email confirmed in Supabase Auth.
    const confirmed = await confirmEmailWithRetry(userId);
    if (!confirmed.ok) {
      await logVerificationEvent(userId, "verification_auth_confirm_failed", {
        message: confirmed.message,
        status: confirmed.status,
        attempts: confirmed.attempts,
      });
      return {
        ok: false,
        reason: "confirm_failed",
        message: CONFIRM_FAILED_MESSAGE,
      };
    }

    // Convenience only: mint a one-time magic link so opening the confirmation
    // link on any browser or device also signs the user in. When the signup
    // started from an OAuth consent screen, land back there instead.
    const { actionLink, magiclinkFailed } = await mintSignInLink(
      userId,
      storedRedirect,
    );
    return {
      ok: true,
      userId,
      actionLink,
      magiclinkFailed,
      redirectTo: storedRedirect,
    };

  });

export async function assertEmailVerified(userId: string): Promise<void> {
  const { data: prof, error } = await supabaseAdmin
    .from("profiles")
    .select("email_verified")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw new Error(`profile lookup failed: ${error.message}`);
  if ((prof?.email_verified as boolean | undefined) !== true) {
    throw emailUnverifiedError();
  }
}

export type ResendSignupVerificationResult =
  | { ok: true; sent: true }
  | {
      ok: false;
      reason: "not_found" | "already_verified" | "rate_limited" | "invalid";
      message: string;
    };

// Shared server-side helper: look up an unverified user by email, enforce
// the 60s per-user rate limit, supersede prior codes, insert a fresh
// hashed token, and send the confirmation link directly through Resend.
// Returns a discriminated result so the caller can decide what to surface.
type InternalSendOutcome =
  | { kind: "sent" }
  | { kind: "not_found" }
  | { kind: "already_verified" }
  | { kind: "rate_limited" }
  | { kind: "send_failed"; message: string };

async function sendVerificationLinkByEmail(
  rawEmail: string,
  rawRedirectTo?: string | null,
): Promise<InternalSendOutcome> {
  const email = rawEmail.trim().toLowerCase();
  const redirectTo = sanitizeConsentRedirect(rawRedirectTo);
  if (!email) return { kind: "not_found" };


  // Look up the auth user by email. Admin listUsers with a filter is
  // supported by Supabase; fall back to a scan of the first page if
  // the filter is unavailable.
  let userId: string | null = null;
  try {
    const { data: usersRes } = await supabaseAdmin.auth.admin.listUsers({
      page: 1,
      perPage: 200,
    });
    const match = usersRes?.users?.find(
      (u) => (u.email ?? "").toLowerCase() === email,
    );
    userId = match?.id ?? null;
  } catch {
    userId = null;
  }
  if (!userId) return { kind: "not_found" };

  const { data: prof } = await supabaseAdmin
    .from("profiles")
    .select("email_verified")
    .eq("user_id", userId)
    .maybeSingle();
  if ((prof?.email_verified as boolean | undefined) === true) {
    return { kind: "already_verified" };
  }

  // Per email address ceiling, independent of the per-user cadence below,
  // so the unauthenticated form cannot be used to flood an inbox.
  const oneHourAgo = new Date(Date.now() - 60 * 60_000).toISOString();
  const { count: recentSends } = await supabaseAdmin
    .from("email_send_log")
    .select("id", { count: "exact", head: true })
    .eq("template_name", "email_verification")
    .eq("recipient_email", email)
    .eq("status", "sent")
    .gte("created_at", oneHourAgo);
  if ((recentSends ?? 0) >= RESEND_MAX_PER_HOUR) {
    return { kind: "rate_limited" };
  }

  const { data: recent } = await supabaseAdmin
    .from("email_verification_codes")
    .select("created_at")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (recent?.created_at) {
    const ageMs = Date.now() - new Date(recent.created_at as string).getTime();
    if (ageMs < RATE_LIMIT_SECONDS * 1000) {
      return { kind: "rate_limited" };
    }
  }

  await supabaseAdmin
    .from("email_verification_codes")
    .update({ consumed_at: new Date().toISOString() })
    .eq("user_id", userId)
    .is("consumed_at", null);

  const token = generateToken();
  const tokenHash = hashToken(token);
  const expiresAt = new Date(Date.now() + CODE_TTL_MINUTES * 60_000).toISOString();
  const { error: insertErr } = await supabaseAdmin
    .from("email_verification_codes")
    .insert({
      user_id: userId,
      code: null as unknown as string,
      token_hash: tokenHash,
      expires_at: expiresAt,
      ...(redirectTo ? { redirect_to: redirectTo } : {}),
    });
  if (insertErr) {
    return { kind: "send_failed", message: insertErr.message };
  }

  // Attribution, failure-isolated: never block signup or sending.
  if (redirectTo) {
    try {
      const { data: prof } = await supabaseAdmin
        .from("profiles")
        .select("signup_source")
        .eq("user_id", userId)
        .maybeSingle();
      if (!(prof as { signup_source?: string | null } | null)?.signup_source) {
        await supabaseAdmin
          .from("profiles")
          .update({ signup_source: "assistant_connect" } as never)
          .eq("user_id", userId);
      }
    } catch {
      // attribution must never break verification
    }
  }


  const verifyUrl = buildVerifyUrl(token);
  const html = await render(
    EmailVerificationEmail({ verifyUrl, recipient: email }),
  );

  const resendKey = process.env.RESEND_API_KEY_1 ?? process.env.RESEND_API_KEY;
  const lovableKey = process.env.LOVABLE_API_KEY;
  if (!resendKey || !lovableKey) {
    await supabaseAdmin.from("email_send_log").insert({
      template_name: "email_verification",
      recipient_email: email,
      status: "failed",
      error_message: "missing_email_config",
    });
    return { kind: "send_failed", message: "email_service_not_configured" };
  }

  try {
    const res = await fetch(RESEND_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${lovableKey}`,
        "X-Connection-Api-Key": resendKey,
      },
      body: JSON.stringify({
        from: FROM_ADDRESS,
        to: [email],
        subject: emailVerificationSubject,
        html,
        reply_to: REPLY_TO,
      }),
    });
    const body = await res.text();
    if (!res.ok) {
      await supabaseAdmin.from("email_send_log").insert({
        template_name: "email_verification",
        recipient_email: email,
        status: "failed",
        error_message: body.slice(0, 500),
        metadata: { http_status: res.status, provider: "resend" } as never,
      });
      return { kind: "send_failed", message: `resend_${res.status}` };
    }
    let providerId: string | null = null;
    try {
      providerId = (JSON.parse(body) as { id?: string }).id ?? null;
    } catch {
      providerId = null;
    }
    await supabaseAdmin.from("email_send_log").insert({
      template_name: "email_verification",
      recipient_email: email,
      status: "sent",
      message_id: providerId,
      metadata: { provider: "resend" } as never,
    });
    return { kind: "sent" };
  } catch (err) {
    const msg = err instanceof Error ? err.message : "send_failed";
    await supabaseAdmin.from("email_send_log").insert({
      template_name: "email_verification",
      recipient_email: email,
      status: "failed",
      error_message: msg.slice(0, 500),
    });
    return { kind: "send_failed", message: msg };
  }
}

// Public (unauthenticated) send called immediately after signUp() succeeds.
// We already know the email is valid because the client just created it.
// Awaited so the UI only shows the "Check your email" screen after Resend
// has accepted the message; on failure the caller surfaces a soft error.
export type SendSignupVerificationResult =
  | { ok: true }
  | { ok: false; message: string };

export const sendSignupVerification = createServerFn({ method: "POST" })
  .inputValidator((input: { email: string; redirectTo?: string }) => {
    const email =
      typeof input?.email === "string" ? input.email.trim().toLowerCase() : "";
    if (!email || !email.includes("@")) {
      throw new Error("Enter a valid email address.");
    }
    return { email, redirectTo: sanitizeConsentRedirect(input?.redirectTo) };
  })
  .handler(async ({ data }): Promise<SendSignupVerificationResult> => {
    const outcome = await sendVerificationLinkByEmail(
      data.email,
      data.redirectTo,
    );

    if (outcome.kind === "sent") return { ok: true };
    if (outcome.kind === "already_verified" || outcome.kind === "not_found") {
      // Do not leak account existence; treat as success from the client's view.
      return { ok: true };
    }
    if (outcome.kind === "rate_limited") {
      // A prior send is already in flight; do not surface as an error.
      return { ok: true };
    }
    return {
      ok: false,
      message:
        "We are having trouble sending the confirmation email. Try Resend in a moment.",
    };
  });

// Public (unauthenticated) resend used by the "Check your email" screen
// button. Always reports success so we do not enumerate accounts. Rate
// limiting and user lookup happen inside the shared helper.
export const resendSignupVerification = createServerFn({ method: "POST" })
  .inputValidator((input: { email: string }) => {
    const email =
      typeof input?.email === "string" ? input.email.trim().toLowerCase() : "";
    if (!email || !email.includes("@")) {
      const err = new Error("Enter the email address you signed up with.") as Error & {
        code?: string;
      };
      err.code = "invalid";
      throw err;
    }
    return { email };
  })
  .handler(async ({ data }): Promise<ResendSignupVerificationResult> => {
    await sendVerificationLinkByEmail(data.email);
    return { ok: true, sent: true };
  });
